import React, { useEffect, useState } from "react";
import { ATTENDANCE_STATUS_OPTIONS } from "constants/status";
import { defaultTableOption, rowPerPage } from "constants/table";
import CustomDataTable from "components/customs/table";
import { ArrowLeft } from "react-feather";
import { useLocation } from "react-router";
import { genericGet, genericPut } from "services/services.general";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import SearchBarComponent from "components/customs/searchbarV2";
import DeclineModal from "components/customs/declineModal";
import styled from "styled-components";
import { ACCOUNT_COS } from "utility/constants";
import { Button } from "reactstrap";
import { HeaderText } from "assets/js/Styles";
import eventCongressmanColumns from "./columns";

const TableSection = styled.div`
  margin-top: 16px;
`;

const StyledCongressmanSection = styled.div`
  margin-top: 44px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  h3 {
    margin: 0;
  }
`;

const GoBackButton = styled(Button)`
  margin-right: 16px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 50px;
`;

const SearchbarWrapper = styled.div`
  width: 25%;
  position: relative;
  justify-self: flex-end;

  input {
    border: 2px solid ${({ theme }) => theme.colors.black60};
    background: white;
    box-shadow: none;
    border-radius: 9px;
    height: clamp(35px, 5vw, 50px);
  }
`;

const CongressmanEvents = () => {
  const location = useLocation();
  const congressman = location.state?.row;

  const cosPosition =
    congressman.position === ACCOUNT_COS ? congressman.position : null;

  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });

  const [isDeclineModalShown, setIsDeclineModalShown] = useState(false);

  const [congEvent, setCongEvent] = useState(null);
  const [eventTitle, setEventName] = useState(null);
  const [eveId, setEventId] = useState(0);

  const toggleDeclineModal = () => setIsDeclineModalShown(!isDeclineModalShown);

  let changeSearchTimeoutID;

  const onSort = (column, sortDirection) => {
    setTableOption({
      ...tableOption,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    });
  };

  const fetchCongEvents = () => {
    genericGet({
      url: `congressman/${congressman.congressmanId}/events`,
      params: {
        limit: tableOption.currentRowsPerPage,
        offset: tableOption.offset,
        search: tableOption.searchValue,
        column: tableOption.sort.field,
        order: tableOption.sort.direction,
        pos: cosPosition,
      },
      callback: (data) => {
        setTableOption({
          ...tableOption,
          data: data.rows,
          totalRows: data.count,
        });
      },
    });
  };

  const onChangePage = (page) => {
    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 10,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value;

      setTableOption({
        ...tableOption,
        page: 1,
        searchValue: eventValue.trim(),
      });
    }, 1000);
  };

  const handleCloseModal = () => {
    toggleDeclineModal();
  };

  const handleSaveStatusChange = (data) => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    genericPut({
      url: `/eventsparticipant/${data.EventParticipants[0].id}`,
      formData: {
        eventParticipantStatus:
          data?.EventParticipants[0].eventParticipantStatus,
      },
      callback: ({ isSuccess }) => {
        if (isSuccess) {
          fetchCongEvents();
        }
      },
    });
  };

  const handleStatusChange = async (index, option) => {
    const newData = [...tableOption.data];
    const oldValue = newData[index].EventParticipants[0].eventParticipantStatus;
    const newValue = option.value;

    const oldStatus = ATTENDANCE_STATUS_OPTIONS.find(
      (status) =>
        status.value ===
        newData[index].EventParticipants[0].eventParticipantStatus
    ).label;

    if (oldValue.toLowerCase() !== newValue.toLowerCase()) {
      try {
        if (newValue === "Declined") {
          newData[index] = {
            ...newData[index], // Keep all other properties
            EventParticipants: [
              {
                ...newData[index].EventParticipants[0],
                eventParticipantStatus: newValue, // Update the status
              },
            ],
          };
          setEventName(newData[index].eventName);
          setEventId(newData[index].id);
          setCongEvent(newData[index].EventParticipants[0]);
          setIsDeclineModalShown(true);
        } else {
          const result = await Swal.fire({
            title: "Warning",
            // eslint-disable-next-line max-len
            text: `Are you sure you want to change the status from ${oldStatus} to ${option.label}?`,
            icon: "warning",
            confirmButtonText: "OK",
            showCancelButton: true,
          });

          if (result.isConfirmed) {
            newData[index] = {
              ...newData[index], // Keep all other properties
              EventParticipants: [
                {
                  ...newData[index].EventParticipants[0],
                  eventParticipantStatus: newValue, // Update the status
                },
              ],
            };

            setTableOption({
              ...tableOption,
              data: newData,
            });

            handleSaveStatusChange(newData[index], index);
          } else {
            newData[index] = {
              ...newData[index],
              eventParticipantStatus: oldValue,
            };

            setTableOption({
              ...tableOption,
              data: newData,
            });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
  };

  const columns = eventCongressmanColumns({ handleStatusChange });

  useEffect(() => {
    fetchCongEvents();
  }, [
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.searchValue,
    tableOption.sort.direction,
    tableOption.sort.field,
  ]);

  return (
    <>
      <HeaderSection>
        <GoBackButton
          color="primary"
          outline
        >
          <Link
            to="/events"
            state={{
              view: "CongCos",
            }}
          >
            <ArrowLeft />
          </Link>
        </GoBackButton>
        <HeaderText>{"Events"}</HeaderText>
      </HeaderSection>

      <StyledCongressmanSection>
        <h3>
          {congressman.position === ACCOUNT_COS
            ? congressman["Congressman.cosName"]
            : congressman["Congressman.fullName"]}
        </h3>

        <SearchbarWrapper>
          <SearchBarComponent
            onChangeSearch={onChangeSearch}
            isDisabled={tableOption?.isLoading}
            placeholder="Search"
          />
        </SearchbarWrapper>
      </StyledCongressmanSection>

      <TableSection>
        <CustomDataTable
          columns={columns}
          data={tableOption.data}
          noHeader
          paginationPerPage={tableOption.currentRowsPerPage}
          onSort={onSort}
          paginationServer
          paginationTotalRows={tableOption.totalRows}
          progressPending={tableOption.isLoading}
          height="calc(100vh - 200px)"
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
        />
      </TableSection>

      <DeclineModal
        isOpen={isDeclineModalShown}
        event={congEvent}
        handleClose={handleCloseModal}
        fetchDataTable={fetchCongEvents}
        eventName={eventTitle}
        eventId={eveId}
      />
    </>
  );
};

export default CongressmanEvents;
